import {
  iconAlertaErroPreenchido,
  iconAlertaSucessoPreenchido,
  iconNavDownload,
  iconNavRelogio,
} from '@bsds-lib-component/icons/icons';
import { BsdsCheckbox, BsdsIcon, BsdsInput, BsdsModal } from '@bsds-lib-component/react';
import React, { useCallback, useEffect, useId, useMemo, useState } from 'react';

import { ButtonClose, Pagination } from '../../..';
import { useSystemContainer } from '../../../../hooks/useSystemContainer';
import { apiService } from '../../../../services';
import type { AdhesionProposalItem } from '../../../../types/vida/table/adhesionProposalTableType';
import { formatSubestipulanteCodNome } from '../../../../utils/subestipulanteUtil';
import {
  mapHistoricoParecerItem,
  PARECER_STATUS_COLORS,
  PARECER_STATUS_LABELS,
  type ParecerHistoricoItem,
  type ParecerStatusType,
} from '../../../../utils/adhesionProposalDetailUtils';

type AdhesionProposalDetailModalProps = {
  isOpen: boolean;
  proposal: AdhesionProposalItem | null;
  formatCpf: (cpf: string) => string;
  onClose: () => void;
};

const ITEMS_PER_PAGE = 3;

const ParecerStatusBadge: React.FC<{ status: ParecerStatusType }> = ({ status }) => {
  const color = PARECER_STATUS_COLORS[status];
  const label = PARECER_STATUS_LABELS[status];

  const icon = status === 'recusado'
    ? iconAlertaErroPreenchido
    : status === 'aprovado'
      ? iconAlertaSucessoPreenchido
      : iconNavRelogio;

  return (
    <span
      className="inline-flex items-center gap-1.5 text-xs py-1 px-2.5 rounded-full font-medium text-white whitespace-nowrap"
      style={{ backgroundColor: color }}
    >
      <BsdsIcon icon={icon} className="w-3 h-3" aria-hidden="true" />
      {label}
    </span>
  );
};

const SummaryField: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="min-w-0">
    <span className="text-xs text-gray-500 block mb-1">{label}</span>
    <span className="text-sm font-semibold text-gray-900 break-words block">{value}</span>
  </div>
);

const resolveRefusalReason = async (
  refusalCode: string,
  fallback: string
): Promise<string> => {
  try {
    const response = await apiService.adhesionProposal.getPARefusalReasons(undefined, refusalCode);
    const match = response.retorno?.find(
      (reason) => String(reason.codMotivoRecusa) === refusalCode
    );

    return match?.descMotivoRecusa || fallback || '—';
  } catch {
    return fallback || '—';
  }
};

const enrichRefusedItems = async (
  items: ParecerHistoricoItem[]
): Promise<ParecerHistoricoItem[]> => {
  return Promise.all(
    items.map(async (item) => {
      if (item.status !== 'recusado' || !item.refusalCode) {
        return item;
      }

      const refusalType = await resolveRefusalReason(item.refusalCode, item.refusalType);

      return {
        ...item,
        refusalType,
      };
    })
  );
};

const AdhesionProposalDetailModal: React.FC<AdhesionProposalDetailModalProps> = ({
  isOpen,
  proposal,
  formatCpf,
  onClose,
}) => {
  const modalId = useId();
  const observationsId = useId();
  const { setLoading } = useSystemContainer();

  const [currentPage, setCurrentPage] = useState(1);
  const [selectedParecerId, setSelectedParecerId] = useState<number | null>(null);
  const [checkedIds, setCheckedIds] = useState<Set<number>>(new Set());
  const [historico, setHistorico] = useState<ParecerHistoricoItem[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);

  const totalItems = historico.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / ITEMS_PER_PAGE));

  const paginatedHistorico = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return historico.slice(start, start + ITEMS_PER_PAGE);
  }, [currentPage, historico]);

  const selectedParecer = useMemo(
    () => historico.find((item) => item.id === selectedParecerId) ?? null,
    [historico, selectedParecerId]
  );

  const loadHistorico = useCallback(async () => {
    if (!proposal?.id || proposal.numVersaoPa === undefined || proposal.numVersaoPa === null) {
      setHistorico([]);
      setLoadError('Dados insuficientes para consultar o histórico de parecer.');
      return;
    }

    setLoading(true);
    setLoadError(null);

    try {
      const response = await apiService.adhesionProposal.getPAOpinionHistory(
        proposal.id,
        proposal.numVersaoPa
      );

      const mappedItems = (response.retorno ?? []).map((item, index) =>
        mapHistoricoParecerItem(item, index)
      );
      const enrichedItems = await enrichRefusedItems(mappedItems);

      setHistorico(enrichedItems);
      setSelectedParecerId(enrichedItems[0]?.id ?? null);
      setCurrentPage(1);
      setCheckedIds(new Set());
    } catch (err) {
      setHistorico([]);
      setSelectedParecerId(null);
      setLoadError(
        err instanceof Error ? err.message : 'Erro ao carregar histórico de parecer'
      );
    } finally {
      setLoading(false);
    }
  }, [proposal?.id, proposal?.numVersaoPa, setLoading]);

  useEffect(() => {
    if (!isOpen) {
      setHistorico([]);
      setSelectedParecerId(null);
      setCurrentPage(1);
      setCheckedIds(new Set());
      setLoadError(null);
      return;
    }

    void loadHistorico();
  }, [isOpen, loadHistorico]);

  useEffect(() => {
    if (!selectedParecer || selectedParecer.status !== 'recusado' || !selectedParecer.refusalCode) {
      return;
    }

    if (selectedParecer.refusalType && selectedParecer.refusalType !== '—') {
      return;
    }

    let isActive = true;

    const loadSelectedRefusalReason = async () => {
      const refusalType = await resolveRefusalReason(
        selectedParecer.refusalCode as string,
        selectedParecer.refusalType
      );

      if (!isActive) {
        return;
      }

      setHistorico((prev) =>
        prev.map((item) =>
          item.id === selectedParecer.id ? { ...item, refusalType } : item
        )
      );
    };

    void loadSelectedRefusalReason();

    return () => {
      isActive = false;
    };
  }, [selectedParecer]);

  const handleSelectParecer = (item: ParecerHistoricoItem) => {
    setSelectedParecerId(item.id);
  };

  const handleToggleCheck = (id: number) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleDownload = () => {
    console.log('Download da versão importada', proposal?.id);
  };

  const selectedStatusLabel = selectedParecer
    ? PARECER_STATUS_LABELS[selectedParecer.status]
    : '—';

  const selectedRefusalType =
    selectedParecer?.status === 'recusado' ? selectedParecer.refusalType || '—' : '—';

  return (
    <BsdsModal
      isOpen={isOpen}
      onBsdsModalDidDismiss={onClose}
      backdropDismiss
      color="secondary"
      canDismiss
      animated
      showBackdrop
      keepContentsMounted
      platform="web"
      aria-labelledby={modalId}
      aria-describedby={`${modalId}-description`}
    >
      <div
        className="modal-content flex flex-col h-screen max-h-screen md:h-[75vh]"
        role="dialog"
        aria-modal="true"
        aria-labelledby={modalId}
      >
        {proposal && (
          <>
            <div className="relative flex items-center justify-center p-5 pb-4 shrink-0 border-b border-gray-100">
              <div className="absolute top-4 right-4">
                <ButtonClose isModal onClick={onClose} />
              </div>
              <h5 id={modalId} className="text-lg font-semibold text-gray-900 text-center">
                Consultar Proposta de Adesão
              </h5>
            </div>

            <div
              id={`${modalId}-description`}
              className="overflow-y-auto custom-scrollbar flex-1 px-5 py-4 min-h-0 space-y-6"
              role="region"
              aria-label="Detalhes da proposta de adesão"
            >
              <div className="rounded-lg bg-gray-50 p-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                <SummaryField label="Número da Apólice" value={proposal.contractNumber || '—'} />
                <SummaryField
                  label="Sub-Estipulante"
                  value={formatSubestipulanteCodNome(proposal.subCompanyCod, proposal.subCompanyName)}
                />
                <SummaryField label="Proponente" value={proposal.proposerName || '—'} />
                <SummaryField
                  label="CNPJ/CPF"
                  value={formatCpf(proposal.proposerCpf)}
                />
              </div>

              <section aria-label="Histórico de parecer">
                <div className="flex items-center mb-4">
                  <div className="w-[2.5px] h-6 bg-red-600 mr-3" aria-hidden="true" />
                  <h6 className="text-base font-semibold opacity-80">Histórico de Parecer</h6>
                </div>

                {loadError && (
                  <p className="text-sm text-red-600 mb-4" role="alert">
                    {loadError}
                  </p>
                )}

                <div className="w-full overflow-x-auto custom-scrollbar rounded-md border border-gray-100">
                  <table className="min-w-[800px] w-full table-fixed" aria-label="Histórico de parecer">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="px-3 py-3 text-left text-xs font-semibold border-b border-gray-100 w-12" scope="col">
                          <span className="sr-only">Selecionar</span>
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-semibold border-b border-gray-100 w-28" scope="col">Data</th>
                        <th className="px-3 py-3 text-left text-xs font-semibold border-b border-gray-100 w-36" scope="col">Status</th>
                        <th className="px-3 py-3 text-left text-xs font-semibold border-b border-gray-100 w-48" scope="col">Tipo de Recusa</th>
                        <th className="px-3 py-3 text-left text-xs font-semibold border-b border-gray-100" scope="col">Observação</th>
                        <th className="px-3 py-3 text-left text-xs font-semibold border-b border-gray-100 w-36" scope="col">Analista</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50 text-text-color text-xs">
                      {paginatedHistorico.length > 0 ? (
                        paginatedHistorico.map((item) => {
                          const isSelected = selectedParecerId === item.id;
                          const isChecked = checkedIds.has(item.id);

                          return (
                            <tr
                              key={item.id}
                              className={`cursor-pointer transition-colors hover:bg-gray-50 ${isSelected ? 'bg-red-50/40' : ''}`}
                              onClick={() => handleSelectParecer(item)}
                              aria-selected={isSelected}
                            >
                              <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                                <BsdsCheckbox
                                  checked={isChecked}
                                  onBsdsChange={() => handleToggleCheck(item.id)}
                                  size="small"
                                  color="secondary"
                                  aria-label={`Selecionar parecer de ${item.date}`}
                                />
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap">{item.date}</td>
                              <td className="px-3 py-2">
                                <ParecerStatusBadge status={item.status} />
                              </td>
                              <td className="px-3 py-2">{item.refusalType || '—'}</td>
                              <td className="px-3 py-2">
                                <span className="line-clamp-2">{item.observation}</span>
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap">{item.analyst}</td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan={6} className="px-3 py-6 text-center text-gray-500">
                            Nenhum histórico de parecer encontrado
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {totalItems > 0 && (
                  <div className="bsds-mt-16">
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={setCurrentPage}
                      totalItems={totalItems}
                      itemLabel="registros"
                      alwaysShow
                    />
                  </div>
                )}
              </section>

              <section aria-label="Detalhes do parecer selecionado" className="space-y-4 mb-40">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <SummaryField label="Status" value={selectedStatusLabel} />
                  <SummaryField
                    label="Tipo de Recusa"
                    value={selectedRefusalType}
                  />
                </div>

                <BsdsInput
                  value={selectedParecer?.dateTime ?? '—'}
                  label="Data"
                  type="text"
                  color="primary"
                  platform="web"
                  className="bsds-width-100"
                  readonly
                  aria-label="Data do parecer"
                  aria-readonly="true"
                />

                <button
                  type="button"
                  onClick={handleDownload}
                  className="inline-flex items-center gap-2 text-sm font-semibold text-red-600 hover:text-red-700 transition-colors"
                  aria-label="Download da versão importada"
                >
                  <BsdsIcon icon={iconNavDownload} className="w-4 h-4" aria-hidden="true" />
                  Download da Versão Importada
                </button>

                <div>
                  <label htmlFor={observationsId} className="text-sm font-semibold text-gray-900 block mb-2">
                    Observações
                  </label>
                  <textarea
                    id={observationsId}
                    value={selectedParecer?.observation ?? ''}
                    readOnly
                    rows={4}
                    className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-900 resize-y bg-gray-50"
                    aria-label="Observações do parecer"
                    aria-readonly="true"
                  />
                </div>
              </section>
            </div>
          </>
        )}
      </div>
    </BsdsModal>
  );
};

export default AdhesionProposalDetailModal;
